import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {
  DafBu, Entity,
  EntityStatus,
  Fraud,
  FraudStats,
  Organization, Region,
  Roles,
  WorkFlow
} from '@shared/models/fraud.interface';
import {catchError} from 'rxjs/operators';
import {MessageDisplayService} from '@shared/message-display/message-display.service';
import {TranslateService} from '@ngx-translate/core';
import { environment } from '@env/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class FraudService {
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private authService: AuthService,
    private messageDisplayService: MessageDisplayService
  ) {}

  private handleErrors = (error: any): Observable<any> => {
    this.messageDisplayService.error(error.message);
    return throwError(error);
  };

  get_stats(): Observable<FraudStats> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<FraudStats>(environment.apiUrl + '/fraud/stats', { params: params })
      .pipe(catchError(this.handleErrors));
  }

  get_roles(): Observable<Roles> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<Roles>(environment.apiUrl + '/fraud/roles', { params: params })
      .pipe(catchError(this.handleErrors));
  }

  get_daf_bu(): Observable<DafBu[]> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<DafBu[]>(environment.apiUrl + '/fraud/daf_level_3', {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_dg_bu(): Observable<DafBu[]> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<DafBu[]>(environment.apiUrl + '/fraud/dg_level_3', {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_viewer_bu(): Observable<DafBu[]> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<DafBu[]>(environment.apiUrl + '/fraud/viewer_level_3', {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_dg_level_2(): Observable<DafBu[]> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<DafBu[]>(environment.apiUrl + '/fraud/dg_level_2', {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_entity_status(entity_id): Observable<EntityStatus> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<EntityStatus>(
        environment.apiUrl + '/fraud/entity_status/' + entity_id,
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  get_organization_details_by_org(id): Observable<Organization> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<Organization>(environment.apiUrl + '/fraud/organization/' + id, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_regions_by_id(id): Observable<Region[]> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<Region[]>(
        environment.apiUrl + '/fraud/organization/' + id + '/regions',
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  get_entities_by_id(id): Observable<Entity[]> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<Entity[]>(
        environment.apiUrl + '/fraud/organization/' + id + '/entities',
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  get_organization_details_by_cv(code_vector): Observable<Organization> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<Organization>(
        environment.apiUrl + '/fraud/organization_cv/' + code_vector,
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  create_fraud(fraud, filesData) {
    const formData = new FormData();
    formData.append('fraud', JSON.stringify(fraud));

    for (const file of filesData) {
      formData.append('files', file, file.name);
    }
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post(environment.apiUrl + '/fraud/create', formData, { params: params })
      .pipe(catchError(this.handleErrors));
  }

  delete_fraud(id): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .delete<any>(environment.apiUrl + `/fraud/${id}`, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  update_fraud(fraudId, fraud, filesData) {
    const formData = new FormData();
    fraud['fraud_id'] = fraudId;
    formData.append('fraud', JSON.stringify(fraud));
    for (const file of filesData) {
      if (file instanceof File) {
        formData.append('files', file, file.name);
      } else {
        console.error('Invalid file object:', file);
      }
    }
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post(environment.apiUrl + '/fraud/update', formData, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_id_entity_status_created(
    entity_ids: number[],
    daf_no_fraud: boolean,
    dg_no_fraud: boolean,
    first_declaration: boolean,
    metadata: any
  ): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    const declarationType = daf_no_fraud ? 'no_fraud' : 'yes_fraud';

    return this.http
      .post(
        environment.apiUrl + '/fraud/entity_status',
        {
          entity_ids: entity_ids,
          daf_no_fraud: daf_no_fraud,
          dg_no_fraud: dg_no_fraud,
          first_declaration: first_declaration,
          declaration_type: declarationType,
          metadata: metadata,
        },
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  wf_init(fraud_id, metadata: any) {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post(
        environment.apiUrl + '/fraud/create_wf/' + fraud_id,
        { metadata },
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  private buildFormDataForStepValidation(fraud, commentaire_wk, filesData) {
    const formData = new FormData();

    formData.append('fraud', JSON.stringify(fraud));
    formData.append('commentaire_wk', commentaire_wk);

    for (const file of filesData) {
      if (file.size) {
        // new files
        formData.append('files', file, file.name);
      } else {
        // existing files kept (just the name)
        formData.append('files', JSON.stringify(file));
      }
    }
    return formData;
  }

  wf_second_step(commentaire_wk, fraud_id, fraud, filesData, metadata: any) {
    const formData = this.buildFormDataForStepValidation(
      fraud,
      commentaire_wk,
      filesData
    );
    formData.append('metadata', JSON.stringify(metadata));
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post(environment.apiUrl + '/fraud/step_two_wf/' + fraud_id, formData, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  wf_finish_step(commentaire_wk, fraud_id, fraud, filesData, metadata) {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    const formData = this.buildFormDataForStepValidation(
      fraud,
      commentaire_wk,
      filesData
    );
    formData.append('metadata', JSON.stringify(metadata));
    return this.http
      .post(
        environment.apiUrl + '/fraud/step_finish_wf/' + fraud_id,
        formData,
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  reject_wf(commentaire_wk, fraud_id, current_step, metadata) {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post(
        environment.apiUrl + '/fraud/reject/' + fraud_id,
        {
          commentaire_wk: commentaire_wk,
          current_step: current_step,
          metadata,
        },
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  a_completer_wf(commentaire_wk, fraud_id, current_step, metadata) {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post(
        environment.apiUrl + '/fraud/a_completer/' + fraud_id,
        {
          commentaire_wk: commentaire_wk,
          current_step: current_step,
          metadata,
        },
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  get_wf(fraud_id, current_step): Observable<WorkFlow> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<WorkFlow>(
        environment.apiUrl + '/fraud/get_wf/' + fraud_id + '/' + current_step,
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  get_fraud_to_validate(): Observable<Fraud[]> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<Fraud[]>(environment.apiUrl + '/fraud/to_validate', {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_fraud_declared(): Observable<Fraud[]> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<Fraud[]>(environment.apiUrl + '/fraud/declared', { params: params })
      .pipe(catchError(this.handleErrors));
  }

  get_fraud_in_scope(campaignIds: string[]): Observable<Fraud[]> {
    const url = environment.apiUrl + '/fraud/in_scope';
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    return this.http
      .get<Fraud[]>(url, {
        params: { campaign_ids: campaignIds, email: userEmail },
      })
      .pipe(catchError(this.handleErrors));
  }

  get_fraud(fraud_id): Observable<Fraud> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<Fraud>(environment.apiUrl + '/fraud/' + fraud_id, { params: params })
      .pipe(catchError(this.handleErrors));
  }

  get_frauds_from_list(fraud_ids): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post<any>(environment.apiUrl + '/fraud/for_export', fraud_ids, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_entity_tree(): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<any>(environment.apiUrl + '/entity/tree/', { params: params })
      .pipe(catchError(this.handleErrors));
  }

  get_db_organization(): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get<any>(environment.apiUrl + '/entity/org/', { params: params })
      .pipe(catchError(this.handleErrors));
  }

  import_entity_users(fileToUpload: File, entityId: number) {
    const formData: FormData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post(environment.apiUrl + `/entity/${entityId}/user/`, formData, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  get_entity_users(entityId: number): Observable<any> {
    const headers = new HttpHeaders({ Accept: 'application/json' });
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .get(environment.apiUrl + `/entity/${entityId}/user/`, {
        headers: headers,
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  update_entity(entity: any) {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .put<any>(environment.apiUrl + `/entity/${entity.entity_id}/`, entity, {
        params: params,
      })
      .pipe(catchError(this.handleErrors));
  }

  create_entity(entity: any) {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post<any>(environment.apiUrl + '/entity/', entity, { params: params })
      .pipe(
        catchError((error: HttpErrorResponse): Observable<any> => {
          if (
            error.status === 403 &&
            error.error.toString().includes('CODE_VECTOR_ALREADY_EXISTS')
          ) {
            this.messageDisplayService.warn(
              this.translate.instant('key__error_code_vector_already_exists')
            );
            return throwError(error);
          }
          return this.handleErrors(error);
        })
      );
  }

  move_entity(entityId: number, moveIntoId: number) {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http
      .post<any>(
        environment.apiUrl + `/entity/${entityId}/move-level/`,
        { move_into_id: moveIntoId },
        { params: params }
      )
      .pipe(catchError(this.handleErrors));
  }

  getAttachmentFile(fraud_id, filename): Observable<any> {
    const connectedUser = this.authService.getCurrentUser();
    const userEmail = connectedUser.email ? connectedUser.email : '';
    let params = new HttpParams().set('email', userEmail);
    return this.http.get(
      environment.apiUrl + `/fraud/${fraud_id}/attachment/${filename}`,
      { params: params, responseType: 'blob' }
    );
  }

  send_mail_report(payload: { fraud_id: number; comment: string }): Observable<any> {
  const connectedUser = this.authService.getCurrentUser();
  const userEmail = connectedUser.email ? connectedUser.email : '';
  const params = new HttpParams().set('email', userEmail);

  return this.http
    .post<any>(environment.apiUrl + '/fraud/mail-report', payload, { params })
    .pipe(catchError(this.handleErrors));
  }

}
